import { useEffect, useState } from 'react';
import type { WheelObservableState } from '@goodtime/shared';

/**
 * Audit backlog item D3 (2026-05-28) — mobile dev panel.
 *
 * <p>Fixed-position overlay (bottom-left) that renders the current
 * wheel-room state at a glance so a developer or AI session can
 * sanity-check the server's view without diffing SSE payloads or
 * curling the debug endpoints. Mounts only when the page URL
 * contains {@code ?dev=panel} (or any URL where that token appears
 * in the search string).
 *
 * <p>Reads:
 * <ul>
 *   <li>{@code liveState} — the same {@link WheelObservableState}
 *       the rest of the wheel-room UI consumes. Pull it via the
 *       same {@code useWheelRoomStream} hook the lobby uses.</li>
 *   <li>{@code /api/wheel/debug/{roomId}/pending} — polled every
 *       1.5s so the panel surfaces what the harness queue thinks
 *       is next. Optional — fails silently when the harness
 *       endpoint isn't available (production).</li>
 * </ul>
 *
 * <p>Mounts above the PAUSED badge (z-index 110 > the badge's 100)
 * but with {@code pointer-events: auto} on a small "hide" button so
 * a developer can dismiss it mid-session. Otherwise inert — never
 * captures clicks intended for the game UI.
 *
 * <p>Producer note: this is a tool surface, not part of v1. The
 * styling is functional (mono font, dark translucent background,
 * compact rows), not branded.
 */

export interface WheelDevPanelProps {
  /** Snapshot of the live SSE state. Pass through from the host
   *  view (typically `WheelLobbyView` passes its `liveState`). */
  state: WheelObservableState | null | undefined;
  /** Wheel-room UUID — needed to poll the harness pending queue. */
  roomId?: string | null;
}

interface PendingEntry {
  id: string;
  firedAtMs: number;
  type: string;
  args: Record<string, unknown>;
}

/** Token recognised in the URL search string to enable the panel. */
const URL_TOKEN = 'dev=panel';

/** Pending-queue poll cadence. Long enough to not hammer the
 *  endpoint; short enough to feel live during step-by-step work. */
const PENDING_POLL_MS = 1500;

/** Returns true iff the page URL contains {@code ?dev=panel}.
 *  Exported so the host view can short-circuit the entire panel
 *  mount when the flag isn't set. */
export function isDevPanelEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.search.includes(URL_TOKEN);
}

export function WheelDevPanel({ state, roomId }: WheelDevPanelProps) {
  const [pending, setPending] = useState<PendingEntry[]>([]);
  const [hidden, setHidden] = useState(false);

  // Poll the debug controller's /pending endpoint so the panel can
  // show the next-due scheduled command. Fails silently when the
  // endpoint isn't available — production builds gate the
  // controller behind a Spring profile.
  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/wheel/debug/${roomId}/pending`);
        if (!res.ok) return;
        const data = (await res.json()) as PendingEntry[];
        if (!cancelled) setPending(data);
      } catch {
        // Profile-gated endpoint missing or network glitch — leave
        // the previous value in place. The panel still renders the
        // SSE-side data.
      }
    };
    poll();
    const id = window.setInterval(poll, PENDING_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [roomId]);

  if (hidden || !state) return null;

  const seats = state.seats ?? [];
  const dialResult = state.dialResolveResult;
  const ledgerLinesBySeat = dialResult?.ledgerLinesBySeat ?? {};

  return (
    <div
      // R3 capture mode hides this — the panel only mounts when
      // `?dev=panel` is also in the URL, but the hide rule is
      // defensive against a producer leaving that flag on by accident.
      data-capture-hide
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        // Above PAUSED badge (z=100) so both can co-exist; PAUSED
        // sits top-left, panel sits bottom-left.
        zIndex: 110,
        background: 'rgba(8, 4, 22, 0.92)',
        color: '#e9f0ff',
        fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
        fontSize: 11,
        lineHeight: 1.45,
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid rgba(255, 201, 64, 0.4)',
        maxWidth: 380,
        maxHeight: '60vh',
        overflowY: 'auto',
        pointerEvents: 'auto',
        boxShadow: '0 4px 18px rgba(0,0,0,0.55)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 800, color: '#ffc940' }}>
          DEV PANEL
        </span>
        <span style={{ opacity: 0.55, fontSize: 9 }}>?dev=panel</span>
        <button
          type="button"
          onClick={() => setHidden(true)}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            color: 'rgba(233,240,255,0.65)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 4,
            padding: '1px 6px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 9,
          }}
        >
          hide
        </button>
      </div>

      <Row label="phase" value={state.phase} />
      <Row label="version" value={String(state.version)} />
      <Row
        label="manualMode"
        value={state.manualMode ? 'true ⏸' : 'false ▶'}
        accent={state.manualMode ? '#ffc940' : undefined}
      />
      <Row label="matchNumber" value={String(state.matchNumber ?? '—')} />
      <Row label="meter" value={String(state.meter)} />
      <Row label="audienceCount" value={String(state.audienceCount)} />

      <Section title="seats" />
      {seats.map((s) => (
        <div key={s.seatId} style={{ marginLeft: 8 }}>
          <span style={{ color: '#ffc940' }}>seat {s.seatId}</span>{' '}
          <span style={{ opacity: 0.8 }}>{s.kind}</span>{' '}
          {s.kind !== 'EMPTY' && (
            <>
              tokens={s.tokens}{' '}
              {s.pendingStake !== null && s.pendingStake !== undefined && (
                <>stake={s.pendingStake}{' '}</>
              )}
              {s.loadout?.length > 0 && (
                <>loadout=[{s.loadout.join(',')}]{' '}</>
              )}
              {s.pendingDeployments?.length > 0 && (
                <span style={{ color: '#9de54e' }}>
                  pending=[{s.pendingDeployments.join(',')}]
                </span>
              )}
            </>
          )}
        </div>
      ))}

      {dialResult && (
        <>
          <Section title="dial resolve" />
          <Row
            label="landingAngleDeg"
            value={dialResult.landingAngleDeg.toFixed(1)}
          />
          <Row
            label="landingWedgeIndex"
            value={String(dialResult.landingWedgeIndex)}
          />
          {Object.entries(dialResult.perSeatOutcomes).map(([sid, outcome]) => (
            <div key={sid} style={{ marginLeft: 8 }}>
              <span style={{ color: '#ffc940' }}>seat {sid}</span>{' '}
              wedge={outcome.wedge.kind === 'COINS'
                ? `×${outcome.wedge.multiplier ?? 0}`
                : outcome.wedge.kind}{' '}
              payout={outcome.payout}
              {(ledgerLinesBySeat[sid]?.length ?? 0) > 0 && (
                <div style={{ marginLeft: 12, opacity: 0.85 }}>
                  {(ledgerLinesBySeat[sid] ?? []).map((line, i) => (
                    <div key={i}>
                      <span style={{ color: line.delta >= 0 ? '#9de54e' : '#ff8e9d' }}>
                        {line.kind}
                      </span>{' '}
                      {line.itemId}
                      {line.otherSeatId != null && (
                        <> ↔ seat {line.otherSeatId}</>
                      )}{' '}
                      Δ{line.delta}
                      {line.blocked && (
                        <span style={{ color: '#9de54e', marginLeft: 6 }}>BLOCKED</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {pending.length > 0 && (
        <>
          <Section title="pending queue" />
          {pending.map((p) => (
            <div key={p.id} style={{ marginLeft: 8 }}>
              <span style={{ color: '#ffc940' }}>{p.type}</span>{' '}
              fires at +{Math.max(0, p.firedAtMs - Date.now())}ms
              {p.args && Object.keys(p.args).length > 0 && (
                <span style={{ opacity: 0.6 }}>
                  {' '}
                  {JSON.stringify(p.args)}
                </span>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div>
      <span style={{ opacity: 0.55 }}>{label}=</span>
      <span style={{ color: accent ?? '#e9f0ff' }}>{value}</span>
    </div>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 2,
        opacity: 0.65,
        fontSize: 9,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
      }}
    >
      ─── {title} ───
    </div>
  );
}
